import { useCallback, useEffect, useState } from "react"
import { IS_WEB } from "../shared/platform"

/**
 * Liste les micros et caméras disponibles, pour savoir s'il y a réellement le
 * choix. C'est ce qui permet de retirer le barreau « essaie l'autre appareil »
 * quand il n'y en a qu'un — on ne demande jamais un geste impossible.
 *
 * Web seulement : sur mobile, l'énumération ne renvoie pas grand-chose
 * d'exploitable et le seul choix réel est avant / arrière pour la caméra, qui
 * se traite ailleurs.
 */
const sameList = (a, b) =>
    a.length === b.length &&
    a.every((device, index) => device.deviceId === b[index].deviceId && device.label === b[index].label)

const sameDevices = (a, b) =>
    sameList(a.audioinput, b.audioinput) && sameList(a.videoinput, b.videoinput)

export const useMediaDevices = () => {
    const [devices, setDevices] = useState({ audioinput: [], videoinput: [] })

    const refresh = useCallback(async () => {
        if (!IS_WEB || !navigator?.mediaDevices?.enumerateDevices) return
        try {
            const all = await navigator.mediaDevices.enumerateDevices()
            const next = {
                audioinput: all.filter((device) => device.kind === "audioinput"),
                videoinput: all.filter((device) => device.kind === "videoinput"),
            }
            // Deux tableaux neufs à chaque relevé forceraient un rendu complet
            // même quand rien n'a bougé — or c'est le cas le plus fréquent.
            setDevices((previous) => (sameDevices(previous, next) ? previous : next))
        } catch (error) {
            // Pas de liste : on considérera simplement qu'il n'y a pas le choix.
            setDevices({ audioinput: [], videoinput: [] })
        }
    }, [])

    useEffect(() => {
        refresh()
        if (!IS_WEB || !navigator?.mediaDevices) return undefined

        // Un casque branché ou débranché pendant la vérification change la donne.
        navigator.mediaDevices.addEventListener?.("devicechange", refresh)
        return () => navigator.mediaDevices.removeEventListener?.("devicechange", refresh)
    }, [refresh])

    return { devices, refresh }
}
